///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faCircleCheck, faCircleXmark, faClock } from '@fortawesome/free-solid-svg-icons'

import { useAuth } from '../../context/AuthContext'
import { formatNr } from '../../services/FormatService'

//-----------------------------------------------------------------------------------------
//* \brief
//? Where the customer lands after paying by card.
//?
//? The payment notification from NETOPIA and this redirect race each other, and the
//? notification is the only thing that activates a subscription. So this page does not
//? decide anything - it asks the backend what the payment record says, and keeps asking
//? until it settles.
//-----------------------------------------------------------------------------------------

// how long to keep asking before telling the customer we will email them instead
const POLL_INTERVAL_MS = 3000
const MAX_ATTEMPTS = 20

const PaymentResult = () => {

    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { currentUser, fetchCardPaymentStatus } = useAuth()

    const orderID = searchParams.get('order') ||
        (() => {
            // a browser that dropped the query string still has the order we stored
            try { return window.sessionStorage.getItem('lastCardOrder') || '' }
            catch (err) { return '' }
        })()
    //? There is deliberately no "the URL said it was cancelled" branch here. NETOPIA sends
    //? the customer back through its cancel URL even after a successful payment, so the
    //? only thing that can be trusted is the payment record the notification writes.

    const [payment, setPayment] = useState(null)
    const [attempts, setAttempts] = useState(0)
    const [gaveUp, setGaveUp] = useState(false)
    const [notFound, setNotFound] = useState(false)

    //? survives re-renders so the interval never keeps running after we are done
    const stopped = useRef(false)

    useEffect(() => {
        if (orderID === '') return
        if (!currentUser || !currentUser.uid) return

        stopped.current = false
        let attempt = 0

        const poll = async () => {
            if (stopped.current) return

            const result = await fetchCardPaymentStatus(orderID)
            attempt += 1
            setAttempts(attempt)

            if (stopped.current) return

            if (!result.ok) {
                // a 404 right after the redirect is not worth alarming anyone about,
                // but it is if it persists
                if (attempt >= 3) setNotFound(true)
            }
            else {
                setPayment(result)
                setNotFound(false)
                const settled = result.status === 'paid' ||
                    result.status === 'failed' ||
                    result.status === 'reversed' ||
                    result.status === 'paid_unmatched'
                if (settled) {
                    stopped.current = true
                    return
                }
            }

            if (attempt >= MAX_ATTEMPTS) {
                stopped.current = true
                setGaveUp(true)
                return
            }
            setTimeout(poll, POLL_INTERVAL_MS)
        }

        poll()
        return () => { stopped.current = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderID, currentUser && currentUser.uid])

    const goToApp = () => navigate('/')

    //? Standalone route, outside MainApp: it cannot inherit colours from the app shell the
    //? way the in-app sections do, so the page carries its own.
    const shell = (children) => (
        <div className='payment-result'>
            <div className='payment-result__card'>
                {children}
            </div>
        </div>
    )

    // ---------------------------------------------------------------- no order
    if (orderID === '') {
        return shell(
            <>
                <h2>Plată necunoscută</h2>
                <p>Nu am putut identifica plata. Verificați starea comenzii în secțiunea "Plăți".</p>
                <button className='md-btn md-btn--primary' onClick={goToApp}>Înapoi în aplicație</button>
            </>
        )
    }

    // ------------------------------------------------------- waiting for auth
    if (!currentUser || !currentUser.uid) {
        return shell(
            <>
                <h2>Se verifică plata</h2>
                <p><FontAwesomeIcon className='spinner' icon={faSpinner}/> Se încarcă sesiunea...</p>
                <p className='ap-popup-hint'>
                    Dacă nu sunteți autentificat, conectați-vă și verificați comanda în secțiunea "Plăți".
                </p>
            </>
        )
    }

    const status = payment ? payment.status : ''

    // ------------------------------------------------------------------- paid
    if (status === 'paid') {
        return shell(
            <>
                {/* the theme green, not the CSS keyword: plain `green` is near invisible
                    on the dark theme's card */}
                <h2 style={{color:'var(--chart-good)'}}>
                    <FontAwesomeIcon icon={faCircleCheck}/> Plata a fost efectuată
                </h2>
                <p>Vă mulțumim! Abonamentul <b>{payment.subscriptionName}</b> este <b>activ</b>.</p>
                <div className='md-summary' style={{marginBlock:'1em'}}>
                    <div className='md-summary__item'>
                        <span>Sumă achitată</span>
                        <span>{formatNr(payment.amount)} {payment.currency}</span>
                    </div>
                    {payment.invoiceSeriesNumber &&
                        <div className='md-summary__item'>
                            <span>Factură</span>
                            <span>{payment.invoiceSeriesNumber}</span>
                        </div>
                    }
                </div>
                <p>
                    Factura este disponibilă în secțiunea <b>"Plăți"</b> și v-am trimis
                    confirmarea și pe email.
                </p>
                <button className='md-btn md-btn--primary' onClick={goToApp}>
                    Începeți să folosiți abonamentul
                </button>
            </>
        )
    }

    // ---------------------------------------------------------------- failed
    if (status === 'failed' || status === 'reversed') {
        return shell(
            <>
                <h2 style={{color:'var(--chart-bad, red)'}}>
                    <FontAwesomeIcon icon={faCircleXmark}/> Plata nu a fost finalizată
                </h2>
                <p>{(payment && payment.message) || 'Plata nu a putut fi procesată.'}</p>
                <p><b>Nu a fost reținută nicio sumă</b> de pe cardul dumneavoastră.</p>
                <div className='ap-popup-info' style={{textAlign:'left', marginBlock:'1em'}}>
                    <div>Comanda dumneavoastră este în continuare valabilă. Din secțiunea <b>"Plăți"</b> puteți:</div>
                    <div style={{marginTop:'0.4em'}}>- reîncerca plata cu cardul;</div>
                    <div>- plăti prin transfer bancar;</div>
                    <div>- anula comanda, dacă v-ați răzgândit.</div>
                </div>
                <button className='md-btn md-btn--primary' onClick={goToApp}>Înapoi în aplicație</button>
            </>
        )
    }

    // ------------------------------------------------ paid but needs a human
    if (status === 'paid_unmatched') {
        return shell(
            <>
                <h2><FontAwesomeIcon icon={faClock}/> Plata se verifică</h2>
                <p>
                    Plata a fost înregistrată, dar necesită o verificare din partea noastră înainte de
                    activarea abonamentului. Am fost deja notificați și vă contactăm în cel mai scurt timp.
                </p>
                <button className='md-btn md-btn--primary' onClick={goToApp}>Înapoi în aplicație</button>
            </>
        )
    }

    // ------------------------------------------------------------ not found
    if (notFound) {
        return shell(
            <>
                <h2>Plata nu a fost găsită</h2>
                <p>Nu am găsit această plată în contul dumneavoastră. Verificați starea comenzii în secțiunea "Plăți".</p>
                <button className='md-btn md-btn--primary' onClick={goToApp}>Înapoi în aplicație</button>
            </>
        )
    }

    // --------------------------------------------------- still being processed
    return shell(
        <>
            <h2><FontAwesomeIcon className='spinner' icon={faSpinner}/> Se confirmă plata</h2>
            <p>
                Am primit plata și așteptăm confirmarea de la procesator. Durează de obicei
                câteva secunde - <b>nu închideți această pagină</b>.
            </p>
            {gaveUp &&
                <div className='ap-popup-warning' style={{textAlign:'left', marginBlock:'1em'}}>
                    <div>
                        Confirmarea durează mai mult decât de obicei. <b>Nu este nevoie să plătiți din nou.</b>
                    </div>
                    <div style={{marginTop:'0.4em'}}>
                        Dacă ați renunțat la plată sau aceasta nu a reușit, <b>nu s-a reținut nicio sumă</b>,
                        iar comanda rămâne valabilă în secțiunea "Plăți".
                    </div>
                    <div style={{marginTop:'0.4em'}}>
                        Dacă plata a fost efectuată, abonamentul se activează automat imediat ce primim
                        confirmarea, iar dacă ceva nu este în regulă vă contactăm pe email.
                    </div>
                </div>
            }
            <p className='ap-popup-hint'>Verificare {attempts}/{MAX_ATTEMPTS}</p>
            <button className='md-btn md-btn--ghost' onClick={goToApp}>Înapoi în aplicație</button>
        </>
    )
}

export default PaymentResult
